import { DATE_FORMAT } from "@/constants/datetime";
import { EMatchContractStatus } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { validator } from "@/utils/validator";

export const matchSchema = validator.object().shape({
  matchName: validator.string().trim().emptyToNull().max(60).label("会社名").required(),
  matchLicenseNumber: validator
    .string()
    .trim()
    .emptyToNull()
    .label("紹介事業許可番号")
    .required()
    .matches(/^\d{2}-ユ-\d{6}$/, "無効な紹介事業許可番号の形式です。期待される形式: YY-ユ-XXXXXX"),
  contractStatus: validator
    .string()
    .isSelection()
    .emptyToNull()
    .label("ステータス")
    .test((value, ctx) => {
      const todayStr = dayjs().format(DATE_FORMAT);
      const trialEndDateStr = ctx.parent.trialEndDate ? dayjs(ctx.parent.trialEndDate).format(DATE_FORMAT) : null;
      const contractEndDateStr = ctx.parent.contractEndDate ? dayjs(ctx.parent.contractEndDate).format(DATE_FORMAT) : null;
      const contractStartDateStr = ctx.parent.contractStartDate ? dayjs(ctx.parent.contractStartDate).format(DATE_FORMAT) : null;
      if (
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === EMatchContractStatus.TRIAL &&
        trialEndDateStr &&
        dayjs(todayStr).isAfter(dayjs(trialEndDateStr))
      ) {
        return ctx.createError({ message: "トライアル終了日が過ぎているため、ステータスを「トライアル中」に設定することはできません。" });
      }
      if (
        ctx.parent.contractStatus &&
        [EMatchContractStatus.TRIAL, EMatchContractStatus.DURING_CONTRACT].includes(ctx.parent.contractStatus) &&
        contractEndDateStr &&
        dayjs(todayStr).isAfter(dayjs(contractEndDateStr))
      ) {
        return ctx.createError({ message: "解約日を過ぎているため、ステータスを「トライアル中」または「契約中」に設定することはできません。" });
      }
      if (
        !ctx.parent.trialEndDate &&
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === EMatchContractStatus.TRIAL &&
        contractStartDateStr &&
        dayjs(todayStr).isSameOrAfter(contractStartDateStr)
      ) {
        return ctx.createError({ message: "契約開始日が本日または過去の日付の場合、「トライアル」ステータスは選択できません。" });
      }
      if (
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === EMatchContractStatus.TRIAL_CANCELLED &&
        contractStartDateStr &&
        dayjs(contractStartDateStr).isSameOrBefore(dayjs(todayStr))
      ) {
        return ctx.createError({ message: "契約開始日が本日または過去の日付の場合、「トライアル解約済み」ステータスは選択できません。" });
      }
      return true;
    })
    .required(),
  contractPlan: validator.string().trim().emptyToNull().label("契約プラン").max(255).notRequired(),
  trialStartDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("トライアル開始日")
    .isBeforeDate("trialEndDate", { startLabel: "トライアル開始日", endLabel: "トライアル終了日" })
    .notRequired(),
  trialEndDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .isAfterDate("trialStartDate", { startLabel: "トライアル開始日", endLabel: "トライアル終了日" })
    .isSameOrBeforeDate("contractStartDate", { startLabel: "トライアル終了日", endLabel: "契約日" })
    .label("トライアル終了日")
    .notRequired(),
  contractStartDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("契約日")
    .isSameOrAfterDate("trialEndDate", { startLabel: "トライアル終了日", endLabel: "契約日" })
    .isAfterDate("trialStartDate", { startLabel: "トライアル開始日", endLabel: "契約日" })
    .when("contractStatus", {
      is: EMatchContractStatus.DURING_CONTRACT,
      then: (schema) => schema.required(),
    })
    .notRequired(),
  contractEndDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .isAfterDate("contractStartDate", { startLabel: "契約日", endLabel: "解約日" })
    .label("解約日")
    .notRequired(),
  description: validator.string().trim().emptyToNull().label("紹介会社情報").notRequired().max(255),
  note: validator.string().trim().emptyToNull().label("備考").notRequired().max(255),
});
